import { useEffect, useMemo, useState } from 'react'
import {
  Bot,
  Building2,
  Calendar as CalendarIcon,
  CalendarDays,
  CalendarX,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  User as UserIcon,
  Video,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useProfile } from '@/api/profile'
import { useCalendars } from '@/api/calendars'
import { useAvailableCallerIds } from '@/api/agents'
import {
  useAppointments,
  type Appointment,
  type AppointmentCalendarProvider,
  type AppointmentStatus,
  type AppointmentStatusFilter,
  type AppointmentType,
} from '@/api/appointments'

import { ProviderBadge } from '../calendars/ProviderIcons'
import { AppointmentForm } from './AppointmentForm'
import { AppointmentCancelDialog } from './AppointmentCancelDialog'

const PAGE_SIZE = 10

// =============================================================================
// Helpers locaux
// =============================================================================

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function getDurationMinutes(start: string, end: string): number {
  return Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / 60000,
  )
}

/** The calendar an appointment lives in. */
function providerOf(appt: Appointment): AppointmentCalendarProvider {
  if (appt.google_calendar) return 'google'
  if (appt.square_booking) return 'square'
  if (appt.wix_booking) return 'wix'
  return 'microsoft'
}

function typeIcon(type: AppointmentType) {
  switch (type) {
    case 'in_person':
      return <Building2 className="h-3.5 w-3.5 text-amber-500" />
    case 'video':
      return <Video className="h-3.5 w-3.5 text-purple-500" />
    case 'phone':
    default:
      return <Phone className="h-3.5 w-3.5 text-blue-500" />
  }
}

function StatusBadge({ status, t }: { status: AppointmentStatus; t: ReturnType<typeof useTranslation>['t'] }) {
  const cls: Record<AppointmentStatus, string> = {
    confirmed:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    pending:
      'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    cancelled:
      'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400',
    completed:
      'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400',
  }
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cls[status]}`}
    >
      {t(`appointments.status.${status}`)}
    </span>
  )
}

// =============================================================================
// Vue principale
// =============================================================================

export function AppointmentsListView() {
  const { t, i18n } = useTranslation()
  const dateLocale = i18n.language?.startsWith('en') ? 'en-CA' : 'fr-CA'

  const { data: profile } = useProfile()
  const companyId = profile?.company

  const { data: calendars = [] } = useCalendars(companyId)
  const { data: smsNumbersRaw = [] } = useAvailableCallerIds()
  // Le pattern Next exclut les numéros IVR de la liste SMS — on conserve.
  const smsNumbers = useMemo(
    () => smsNumbersRaw.filter((n) => n.assignment_type !== 'ivr'),
    [smsNumbersRaw],
  )

  // Filtres + pagination (état local — pas dans l'URL pour rester simple).
  const [status, setStatus] = useState<AppointmentStatusFilter>('upcoming')
  const [search, setSearch] = useState('')
  const [provider, setProvider] = useState<'' | AppointmentCalendarProvider>('')
  const [page, setPage] = useState(1)

  // Reset à la page 1 dès qu'un filtre change.
  useEffect(() => {
    setPage(1)
  }, [status, search, provider])

  const { data, isLoading } = useAppointments(companyId, {
    status,
    page,
    pageSize: PAGE_SIZE,
    search,
    provider,
  })

  const appointments = data?.results ?? []
  const total = data?.count ?? 0
  const totalPages = data?.total_pages ?? 1
  const todayCount = data?.today_count ?? 0
  const weekCount = data?.week_count ?? 0
  const cancelledCount = data?.cancelled_count ?? 0

  // Modales
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [cancelTarget, setCancelTarget] = useState<Appointment | null>(null)

  const openCreate = () => {
    setEditing(null)
    setFormOpen(true)
  }
  const openEdit = (appt: Appointment) => {
    setEditing(appt)
    setFormOpen(true)
  }
  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
  }

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(dateLocale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString(dateLocale, {
      hour: '2-digit',
      minute: '2-digit',
    })

  const filterTabs: { key: AppointmentStatusFilter; label: string }[] = [
    { key: 'upcoming', label: t('appointments.filterUpcoming') },
    { key: 'past', label: t('appointments.filterPast') },
    { key: 'cancelled', label: t('appointments.filterCancelled') },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white tracking-tight">
            {t('appointments.title')}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('appointments.subtitle')}
          </p>
        </div>
        <Button
          type="button"
          onClick={openCreate}
          disabled={calendars.length === 0}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('appointments.newAppointment')}
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {t('appointments.kpiToday')}
            </p>
            <CalendarIcon className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">
              {todayCount}
            </h3>
            <span className="text-sm font-medium text-slate-500">
              {t('appointments.kpiAppointments')}
            </span>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {t('appointments.kpiWeek')}
            </p>
            <CalendarDays className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">
              {weekCount}
            </h3>
            <span className="text-sm font-medium text-slate-500">
              {t('appointments.kpiAppointments')}
            </span>
          </div>
        </Card>
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {t('appointments.kpiCancelled')}
            </p>
            <CalendarX className="h-4 w-4 text-slate-400" />
          </div>
          <div className="flex items-baseline gap-2">
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">
              {cancelledCount}
            </h3>
          </div>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Pills de statut */}
          <div className="flex bg-slate-100 dark:bg-slate-900/50 p-1 rounded-lg self-start md:self-auto">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatus(tab.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${
                  status === tab.key
                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('appointments.searchPlaceholder')}
                className="pl-9"
              />
            </div>
            <Select
              value={provider || 'all'}
              onValueChange={(v) =>
                setProvider(v === 'all' ? '' : (v as AppointmentCalendarProvider))
              }
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('appointments.allCalendars')}
                </SelectItem>
                <SelectItem value="google">Google</SelectItem>
                <SelectItem value="microsoft">Microsoft</SelectItem>
                <SelectItem value="square">Square</SelectItem>
                <SelectItem value="wix">Wix</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Empty state — aucun calendrier connecté */}
      {calendars.length === 0 && !isLoading && (
        <Card className="p-8 text-center">
          <CalendarIcon className="h-12 w-12 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-medium">
            {t('appointments.noCalendarTitle')}
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
            {t('appointments.noCalendarSubtitle')}
          </p>
        </Card>
      )}

      {/* Table desktop */}
      <Card className="hidden md:block overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 dark:bg-slate-800/50">
              <tr className="border-b border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wider">
                <th className="px-6 py-3">{t('appointments.colClient')}</th>
                <th className="px-6 py-3">{t('appointments.colDateTime')}</th>
                <th className="px-6 py-3">{t('appointments.colDuration')}</th>
                <th className="px-6 py-3">{t('appointments.colType')}</th>
                <th className="px-6 py-3">{t('appointments.colCalendar')}</th>
                <th className="px-6 py-3">{t('appointments.colCreatedBy')}</th>
                <th className="px-6 py-3">{t('appointments.colStatus')}</th>
                <th className="px-6 py-3 text-right">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
                  </td>
                </tr>
              ) : appointments.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-16">
                    <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                    <p className="text-slate-500 dark:text-slate-400">
                      {t('appointments.emptyState')}
                    </p>
                  </td>
                </tr>
              ) : (
                appointments.map((appt) => {
                  const calProvider = providerOf(appt)
                  const duration = getDurationMinutes(
                    appt.start_time,
                    appt.end_time,
                  )
                  return (
                    <tr
                      key={appt.id}
                      className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      {/* Client */}
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-blue-600/10 text-blue-600 flex items-center justify-center font-bold text-xs">
                            {getInitials(appt.client_name)}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-slate-900 dark:text-white">
                              {appt.client_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {appt.client_phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Date / Time */}
                      <td className="px-6 py-3">
                        <div className="text-sm text-slate-900 dark:text-white">
                          {formatDate(appt.start_time)}
                        </div>
                        <div className="text-xs text-slate-500">
                          {formatTime(appt.start_time)}
                        </div>
                      </td>
                      {/* Duration */}
                      <td className="px-6 py-3 text-sm text-slate-500">
                        {duration} min
                      </td>
                      {/* Type */}
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
                          {typeIcon(appt.appointment_type)}
                          <span>
                            {t(`appointments.type_${appt.appointment_type}`)}
                          </span>
                        </div>
                      </td>
                      {/* Calendar */}
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-slate-900 dark:text-white">
                          <ProviderBadge provider={calProvider} />
                          <span className="truncate">
                            {appt.calendar_name ?? '-'}
                          </span>
                        </div>
                      </td>
                      {/* Created by */}
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-1.5 text-sm text-slate-500">
                          {appt.ai_agent ? (
                            <>
                              <Bot className="h-4 w-4 text-blue-600" />
                              <span>{appt.ai_agent_name}</span>
                            </>
                          ) : (
                            <>
                              <UserIcon className="h-4 w-4 text-slate-400" />
                              <span>
                                {appt.created_by_name ||
                                  t('appointments.manual')}
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      {/* Status */}
                      <td className="px-6 py-3">
                        <StatusBadge status={appt.status} t={t} />
                      </td>
                      {/* Actions */}
                      <td className="px-6 py-3 text-right">
                        {appt.status !== 'cancelled' && (
                          <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => openEdit(appt)}
                              title={t('appointments.edit')}
                              className="text-slate-400 hover:text-blue-600 h-7 w-7 p-0"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => setCancelTarget(appt)}
                              title={t('appointments.cancel')}
                              className="text-slate-400 hover:text-rose-600 h-7 w-7 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="border-t border-slate-200 dark:border-slate-800 px-6 py-3 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('appointments.showing')}{' '}
              <span className="font-medium">
                {(page - 1) * PAGE_SIZE + 1}
              </span>{' '}
              {t('appointments.to')}{' '}
              <span className="font-medium">
                {Math.min(page * PAGE_SIZE, total)}
              </span>{' '}
              {t('appointments.of')}{' '}
              <span className="font-medium">{total}</span>{' '}
              {t('appointments.results')}
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs px-2 text-slate-700 dark:text-slate-300 font-medium tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Card className="p-8 text-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
          </Card>
        ) : appointments.length === 0 ? (
          <Card className="p-8 text-center">
            <CalendarIcon className="h-10 w-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
            <p className="text-slate-500 dark:text-slate-400">
              {t('appointments.emptyState')}
            </p>
          </Card>
        ) : (
          appointments.map((appt) => {
            const calProvider = providerOf(appt)
            const duration = getDurationMinutes(
              appt.start_time,
              appt.end_time,
            )
            return (
              <Card key={appt.id} className="p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-blue-600/10 text-blue-600 flex items-center justify-center font-bold text-sm shrink-0">
                      {getInitials(appt.client_name)}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                        {appt.client_name}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {appt.client_phone}
                      </div>
                    </div>
                  </div>
                  <StatusBadge status={appt.status} t={t} />
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 dark:text-slate-400 mb-3">
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {t('appointments.colDateTime')}
                    </p>
                    <p className="text-slate-900 dark:text-white">
                      {formatDate(appt.start_time)} - {formatTime(appt.start_time)}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {t('appointments.colDuration')}
                    </p>
                    <p>{duration} min</p>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {t('appointments.colType')}
                    </p>
                    <div className="flex items-center gap-1.5">
                      {typeIcon(appt.appointment_type)}
                      <span>{t(`appointments.type_${appt.appointment_type}`)}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-slate-400 text-[10px] uppercase tracking-wider">
                      {t('appointments.colCalendar')}
                    </p>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <ProviderBadge provider={calProvider} />
                      <span className="truncate">
                        {appt.calendar_name ?? '-'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-800">
                  <Badge variant="outline" className="text-[10px] gap-1">
                    {appt.ai_agent ? (
                      <>
                        <Bot className="h-3 w-3 text-blue-600" />
                        {appt.ai_agent_name}
                      </>
                    ) : (
                      <>
                        <UserIcon className="h-3 w-3 text-slate-400" />
                        {appt.created_by_name || t('appointments.manual')}
                      </>
                    )}
                  </Badge>
                  {appt.status !== 'cancelled' && (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(appt)}
                        className="text-slate-400 hover:text-blue-600 h-7 w-7 p-0"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setCancelTarget(appt)}
                        className="text-slate-400 hover:text-rose-600 h-7 w-7 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            )
          })
        )}

        {/* Pagination mobile */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-3 py-3">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {page} / {totalPages}
            </p>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Form (Sheet) */}
      <AppointmentForm
        open={formOpen}
        onClose={closeForm}
        companyId={companyId}
        editing={editing}
        calendars={calendars}
        smsNumbers={smsNumbers}
      />

      {/* Dialog d'annulation */}
      <AppointmentCancelDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        companyId={companyId}
        appointment={cancelTarget}
      />
    </div>
  )
}
